import type { LookupItem, UserResponse } from '@/types/shared';

/**
 * T175 (§20, `docs/design/19-branch-scoping-ux.md` §5.2) — the single derived boolean every
 * scoped list page uses to decide whether to render a Branch column + filter at all:
 *
 *   const showBranchColumn = currentUser.allBranches || currentUser.branches.length > 1;
 *
 * A single-branch user sees neither a column with one repeated value nor a filter whose dropdown
 * has exactly one option ("a filter with one option is noise" — T159 §5.2). Computed once from
 * the signed-in user, never from the list response itself.
 */
export function hasMultipleBranches(user: UserResponse | null | undefined): boolean {
  if (!user) return false;
  return user.allBranches || user.branches.length > 1;
}

/**
 * T175 (DEC-BR10, `docs/design/19-branch-scoping-ux.md` §3.1) — resolves the ONE branch a
 * single-branch (non-all-branches) user is scoped to, or `null` for every other case (all-
 * branches, multi-branch, or no user yet). Registration/create forms use this to decide whether
 * to render the branch field at all: `null` -> render a required select; a `LookupItem` -> hide
 * the field entirely and let the server default it (FR-18 — omitting `branchId` on the wire is
 * how the "no added friction" requirement is satisfied, not a client-side auto-fill of the id).
 */
export function resolveSingleBranch(user: UserResponse | null | undefined): LookupItem | null {
  if (!user) return null;
  if (user.allBranches) return null;
  return user.branches.length === 1 ? user.branches[0] : null;
}
